import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Trash2, FolderTree, Download, Upload } from 'lucide-react'
import { ImportSubjectModal } from '@/components/admin/ImportSubjectModal'


interface SubjectItem {
  id: number
  name: string
  code: string
  chapters_count: number
  sections_count: number
}

interface Props {
  subjects: SubjectItem[]
}

export default function SubjectsIndex({ subjects }: Props) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [importModalOpen, setImportModalOpen] = useState(false)

  const handleCreate = (e: React.FormEvent) => {

    e.preventDefault()
    if (!name.trim() || !code.trim()) return
    router.post('/admin/subjects', {
      subject: {
        name: name.trim(),
        code: code.trim().toUpperCase(),
      }
    }, {
      onSuccess: () => {
        setName('')
        setCode('')
      }
    })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure you want to delete this subject and its chapters?')) return
    router.delete(`/admin/subjects/${id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold">Taxonomy Management</h1>
          <p className="text-muted-foreground mt-1">Manage subjects, chapters, and sections</p>
        </div>

        {/* Create form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Add Subject</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5 flex-1 min-w-[200px]">
                <label className="text-sm font-medium">Subject Name *</label>
                <Input
                  placeholder="e.g. Physics"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5 w-36">
                <label className="text-sm font-medium">Code *</label>
                <Input
                  placeholder="e.g. PHY"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  required
                />
              </div>

              <Button type="submit" disabled={!name.trim() || !code.trim()}>
                Add Subject
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Subjects ({subjects.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 font-medium"
                onClick={() => setImportModalOpen(true)}
              >
                <Upload className="h-4 w-4 text-muted-foreground" />
                Import JSON
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 font-medium" asChild>
                <a href="/admin/subjects/export">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  Export JSON
                </a>
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject Name</TableHead>
                  <TableHead className="w-28">Code</TableHead>
                  <TableHead className="w-36">Chapters</TableHead>
                  <TableHead className="w-36">Sections</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No subjects created yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  subjects.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-semibold">
                        <Link
                          href={`/admin/subjects/${s.id}`}
                          className="hover:underline flex items-center gap-1.5 text-primary"
                        >
                          <FolderTree className="h-4 w-4 text-muted-foreground" />
                          {s.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono uppercase font-semibold">
                          {s.code}
                        </Badge>
                      </TableCell>
                      <TableCell>{s.chapters_count}</TableCell>
                      <TableCell>{s.sections_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Button size="icon" variant="ghost" asChild title="Manage Subject Chapters">
                            <Link href={`/admin/subjects/${s.id}`}>
                              <FolderTree className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(s.id)}
                            title="Delete Subject"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Import Modal */}
        <ImportSubjectModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
        />
      </div>
    </AppShell>
  )
}

